import { useState, useMemo } from "react";
import { Box, Button, Chip, IconButton, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { Delete, Edit, DragIndicator, CheckCircle, Cancel } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useGetTeamsQuery, useDeleteTeamMutation, useReorderTeamProfilesMutation, useActivateTeamMutation, useDeactivateTeamMutation } from "./api/teamApi";
import { TeamProfile } from "./types/team";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditTeamProfile from "./components/AddEditTeam";
// import AddEditTeamProfile from "./components/AddEditTeamProfile";

// Sortable Row Component
const SortableRow: React.FC<{
  profile: TeamProfile;
  onEdit: (profile: TeamProfile) => void;
  onDelete: (profile: TeamProfile) => void;
  onActivate: (profile: TeamProfile) => void;
  onDeactivate: (profile: TeamProfile) => void;
}> = ({ profile, onEdit, onDelete, onActivate, onDeactivate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: profile.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{ 
        cursor: 'grab',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
    >
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab' }}
          >
            <DragIndicator />
          </IconButton>
          <Typography variant="body2" fontWeight="medium">
            {profile.order_number || 0}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <img
          src={profile.profile_image}
          alt="Profile"
          width={40}
          height={40}
          style={{ borderRadius: 6, objectFit: 'cover' }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {profile.full_name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {profile.position}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography 
          variant="body2" 
          sx={{ 
            maxWidth: 200, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: (profile.bio && profile.bio !== 'null') ? 'text.primary' : 'text.disabled'
          }}
        >
          {(profile.bio && profile.bio !== 'null') ? profile.bio : 'No bio provided'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          icon={Boolean(profile.is_active) ? <CheckCircle /> : <Cancel />}
          label={Boolean(profile.is_active) ? 'Active' : 'Inactive'}
          color={Boolean(profile.is_active) ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Box display="flex" gap={1}>
          {Boolean(profile.is_active) ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeactivate(profile);
              }}
              color="warning"
              title="Deactivate"
            >
              <Cancel />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onActivate(profile);
              }}
              color="success"
              title="Activate"
            >
              <CheckCircle />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(profile);
            }}
            color="error"
          >
            <Delete />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(profile);
            }}
            color="primary"
          >
            <Edit />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const TeamProfileManager: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<TeamProfile | null>(null);

  const { data: profiles, isLoading, isError } = useGetTeamsQuery();
  const [deleteTeamProfile] = useDeleteTeamMutation();
  const [reorderTeamProfiles] = useReorderTeamProfilesMutation();
  const [activateTeam] = useActivateTeamMutation();
  const [deactivateTeam] = useDeactivateTeamMutation();

  // Sort profiles by order_number
  const sortedProfiles = useMemo(() => {
    if (!profiles?.data) return [];
    return [...profiles.data].sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
  }, [profiles?.data]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortedProfiles.findIndex((item) => item.id === active.id);
      const newIndex = sortedProfiles.findIndex((item) => item.id === over?.id);

      const newOrder = arrayMove(sortedProfiles, oldIndex, newIndex);
      
      // Create orders array for API call
      const orders = newOrder.map((profile: TeamProfile, index: number) => ({
        id: profile.id,
        order_number: index + 1
      }));

      try {
        await reorderTeamProfiles({ orders }).unwrap();
      } catch (error) {
        console.error('Failed to reorder team profiles:', error);
      }
    }
  };

  const handleEdit = (profile: TeamProfile) => {
    setSelectedProfile(profile);
    setOpenDialog(true);
  };

  const handleDelete = (profile: TeamProfile) => {
    setSelectedProfile(profile);
    setOpenConfirmModal(true);
  };

  const handleActivate = async (profile: TeamProfile) => {
    try {
      await activateTeam(profile.id).unwrap();
    } catch (error) {
      console.error('Failed to activate team profile:', error);
    }
  };

  const handleDeactivate = async (profile: TeamProfile) => {
    try {
      await deactivateTeam(profile.id).unwrap();
    } catch (error) {
      console.error('Failed to deactivate team profile:', error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading team profiles...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Error loading team profiles</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" alignItems="center" width="100%" mb={3}>
        <Box display="flex" gap={3} alignItems="center" flex="1">
          <Typography variant="h6">Team Profiles</Typography>
          <Chip
            color="primary"
            variant="outlined"
            label={`${sortedProfiles.length} Profiles`}
          />
        </Box>
        <Button
          onClick={() => {
            setSelectedProfile(null);
            setOpenDialog(true);
          }}
          sx={{ minWidth: "200px" }}
          variant="contained"
        >
          Add New Profile
        </Button>
      </Box>

      {/* Drag and Drop Table */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Bio</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext
                items={sortedProfiles.map(profile => profile.id)}
                strategy={verticalListSortingStrategy}
              >
                {sortedProfiles.map((profile) => (
                  <SortableRow
                    key={profile.id}
                    profile={profile}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onActivate={handleActivate}
                    onDeactivate={handleDeactivate}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>

      <AddEditTeamProfile
        open={openDialog}
        profile={selectedProfile}
        setModalOpen={setOpenDialog}
      />

      {selectedProfile && (
        <ConfirmModal
          type="warning"
          title="Delete Profile"
          message={`Are you sure you want to delete '${selectedProfile.full_name}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteTeamProfile(selectedProfile.id.toString());
            setOpenConfirmModal(false);
            setSelectedProfile(null);
          }}
        />
      )}
    </Box>
  );
};

export default TeamProfileManager;
